import React, { useState, useEffect } from 'react';
import Header from '../../components/2025/Header';
import { usePageMeta } from '../../hooks/usePageMeta';
import Hero from '../../components/2025/Hero';
import Thema from '../../components/2025/Thema';
import Title from '../../components/2025/TItle';
import Footer from '../../components/2025/Footer';
import EventImageSection from '../../components/2025/EventImageSection';
import NewsSection from '../../components/2025/NewsSection';
import PicupEvent from '../../components/2025/PicupEvent';
import FloatingNav from '../../components/2025/FloatingNav';
import ArchiveGallery from '../../components/2025/ArchiveGallery';
import { fetchAndCategorizeEvents, CategorizedEvents } from '../../services/eventService';
import { fetchAndFormatNews, FormattedNewsItem } from '../../services/newsService';
import { fetchAllEvents } from '../../services/eventService';
import styles from './styles.module.scss';
import Button from '../../components/2025/Button';
import History from '../../components/2025/History';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { optimizeImage } from '../../utils/imageOptimizer';

const Page: React.FC = () => {
  const [categorizedEvents, setCategorizedEvents] = useState<CategorizedEvents | null>(null);
  const [newsItems, setNewsItems] = useState<FormattedNewsItem[]>([]);
  const [picupEvents, setPicupEvents] = useState<
    {
      imageUrl: string;
      title: string;
      time: string;
      location: string;
      description: string;
      labels: string[];
    }[]
  >([]);
  const [headerKey, setHeaderKey] = useState(0);

  // viewportアニメーション（Picup以降）
  const { ref: picupRef, isVisible: isPicupVisible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.15,
    triggerOnce: true,
  });
  const { ref: event1Ref, isVisible: isEvent1Visible } = useScrollAnimation<HTMLElement>({
    threshold: 0.15,
    triggerOnce: true,
  });
  const { ref: event2Ref, isVisible: isEvent2Visible } = useScrollAnimation<HTMLElement>({
    threshold: 0.15,
    triggerOnce: true,
  });
  const { ref: messageRef, isVisible: isMessageVisible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.15,
    triggerOnce: true,
  });
  const { ref: liveRef, isVisible: isLiveVisible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.15,
    triggerOnce: true,
  });
  const { ref: historyRef, isVisible: isHistoryVisible } = useScrollAnimation<HTMLDivElement>({
    threshold: 0.15,
    triggerOnce: true,
  });

  usePageMeta({
    title: 'ホーム',
    description:
      '日本工学院専門学校 蒲田キャンパス × 東京工科大学 蒲田キャンパス × 東京工科大学附属日本語学校 「かまた祭2025」の公式サイトです。今年のテーマや開催情報、見どころなど最新情報をお届けします。皆様のご来場を心よりお待ちしております。',
  });

  useEffect(() => {
    // モーダルが閉じたときにHeaderを再レンダリング
    const handleModalClosed = () => {
      setHeaderKey((prev) => prev + 1);
    };

    window.addEventListener('modalClosed', handleModalClosed);

    return () => {
      window.removeEventListener('modalClosed', handleModalClosed);
    };
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await fetchAndCategorizeEvents();
        setCategorizedEvents(events);

        // event_topがtrueのイベントを取得
        const allEventsResponse = await fetchAllEvents();
        const topEvents = allEventsResponse.contents
          .filter((event) => event.event_top === true)
          .map((event) => ({
            imageUrl: optimizeImage(event.event_image.url, { width: 800, quality: 80 }),
            title: event.event_title,
            time: event.event_time,
            location: event.event_location,
            description: event.event_description,
            labels: [...event.event_category, ...event.event_day],
          }));
        setPicupEvents(topEvents);
      } catch (err) {
        console.error('イベントデータの取得に失敗しました:', err);
      }
    };

    const loadNews = async () => {
      try {
        const formattedItems = await fetchAndFormatNews();
        setNewsItems(formattedItems);
      } catch (err) {
        console.error('ニュースデータの取得に失敗しました:', err);
      }
    };

    loadEvents();
    loadNews();
  }, []);

  const getEventImages = (dayKey: '11/2' | '11/3') => {
    if (!categorizedEvents || !categorizedEvents[dayKey]) {
      return [];
    }

    // event_top: trueのイベントのみフィルタリング
    return categorizedEvents[dayKey]
      .filter((event) => event.event_top)
      .slice(0, 5)
      .map((event) => ({
        id: event.id,
        src: optimizeImage(event.event_image.url, { width: 1200, quality: 80 }),
        alt: event.event_title,
      }));
  };

  return (
    <main className={styles.main}>
      <div className={styles.leftCircle}></div>
      <div className={styles.leftStar7000}>
        <svg
          width="67"
          height="68"
          viewBox="0 0 67 68"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M37.5462 1.00361C37.6227 0.866231 37.8302 0.906928 37.8491 1.06301L40.7582 25.0939C40.8162 25.5729 41.1764 25.9601 41.6499 26.053L65.4032 30.7124C65.5575 30.7427 65.5831 30.9531 65.4406 31.0193L43.484 41.212C43.0465 41.4152 42.7896 41.8771 42.8476 42.356L45.7567 66.3869C45.7756 66.5429 45.5835 66.6322 45.4764 66.517L28.9976 48.7848C28.6692 48.4318 28.1508 48.3301 27.7134 48.5329L5.75684 58.7257C5.61425 58.7918 5.47003 58.6366 5.54648 58.4993L17.3185 37.3482C17.5531 36.9267 17.4897 36.402 17.1614 36.0486L0.682503 18.3164C0.575688 18.2013 0.67878 18.0162 0.832998 18.0464L24.5873 22.706C25.0607 22.7987 25.5397 22.5761 25.7743 22.1547L37.5462 1.00361Z"
            fill="white"
            stroke="#1549B6"
            strokeWidth="0.951418"
          />
        </svg>
        <div className={styles.leftStar7000Small}>
          <svg
            width="37"
            height="36"
            viewBox="0 0 37 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M25.2372 0.475528C26.5225 -0.707249 28.5704 0.445747 28.2258 2.15814L26.2915 11.7688C26.1374 12.5346 26.4939 13.3125 27.1746 13.6958L35.7171 18.5052C37.2392 19.3621 36.7755 21.6662 35.0404 21.8675L25.3024 22.9978C24.5265 23.0878 23.8968 23.6673 23.7426 24.4331L21.8084 34.0437C21.4637 35.7561 19.1292 36.0271 18.4015 34.4392L14.3174 25.5271C13.9919 24.8169 13.2462 24.3971 12.4703 24.4871L2.7323 25.6174C0.997223 25.8188 0.0181121 23.6822 1.30345 22.4994L8.5173 15.8612C9.09213 15.3323 9.26097 14.4933 8.93554 13.7832L4.85141 4.8711C4.12372 3.28316 5.85313 1.69175 7.37521 2.54868L15.9177 7.35813C16.5984 7.74137 17.4485 7.6427 18.0233 7.11375L25.2372 0.475528Z"
              fill="#F6B8DF"
            />
          </svg>
        </div>
      </div>
      <div className={styles.leftRect7000}>
        <svg
          width="120"
          height="182"
          viewBox="0 0 120 182"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="-136.733"
            y="138.791"
            width="277.815"
            height="66.0244"
            rx="33.0122"
            transform="rotate(-32.9084 -136.733 138.791)"
            fill="white"
          />
        </svg>
      </div>
      <Header key={headerKey} />
      <Hero />
      <ArchiveGallery />
      <div className={styles.newsSection}>
        <NewsSection newsItems={newsItems.slice(0, 2)} count={2} viewAllHref="/2025/news" />
      </div>
      <Thema />
      <div
        ref={picupRef}
        className={`${styles.picupSection} ${styles.fadeUp} ${isPicupVisible ? styles['fadeUp--visible'] : ''}`}
      >
        <Title tag="h2" align="center">
          PICUP EVENT
        </Title>
        {picupEvents.length > 0 ? (
          <PicupEvent events={picupEvents} />
        ) : (
          <div>
            <p>Comming soon...</p>
          </div>
        )}
        <div className="button__container">
          <div className="button">
            <Button title="全てのイベント一覧を見る" href="/2025/events" fz="14px" />
          </div>
        </div>
      </div>
      <div className={styles.eventContainer}>
        <section
          ref={event1Ref}
          className={`${styles.eventSection} ${styles.fadeLeft} ${isEvent1Visible ? styles['fadeLeft--visible'] : ''}`}
        >
          <div className={styles.eventHeader}>
            <Title tag="h2">EVENT</Title>
            <p className={styles.eventDate}>11.2 Sunday</p>
          </div>
          {categorizedEvents && getEventImages('11/2').length > 0 ? (
            <EventImageSection images={getEventImages('11/2')} />
          ) : (
            <div className={styles.comingSoon}>
              <p>Coming Soon...</p>
            </div>
          )}
          <div className={styles.viewAllButton}>
            <a href="/2025/events" className={styles.viewAllLink}>
              すべて見る
            </a>
          </div>
        </section>

        <section
          ref={event2Ref}
          className={`${styles.eventSection} ${styles.fadeRight} ${isEvent2Visible ? styles['fadeRight--visible'] : ''}`}
        >
          <div className={styles.eventHeader}>
            <Title tag="h2">EVENT</Title>
            <p className={styles.eventDate}>11.3 Monday</p>
          </div>
          {categorizedEvents && getEventImages('11/3').length > 0 ? (
            <EventImageSection images={getEventImages('11/3')} />
          ) : (
            <div className={styles.comingSoon}>
              <p>Coming Soon...</p>
            </div>
          )}
          <div className={styles.viewAllButton}>
            <a href="/2025/events" className={styles.viewAllLink}>
              すべて見る
            </a>
          </div>
        </section>
      </div>
      <div className="comming__block">
        <div
          ref={messageRef}
          className={`${styles.messageSection} ${styles.fadeUp} ${isMessageVisible ? styles['fadeUp--visible'] : ''}`}
        >
          <Title tag="h2" align="center">
            MESSAGE
          </Title>
          <div className={styles.messageContent}>
            <div className={styles.messageText}>
              体制が変わったので完全に手探りではありますね(山尾)。
              <br />
              でも当日来たみんなが楽しめるかまた祭を作りたいという気持ちは変わりません！(川田)。
              <br />
              今年の委員会内の雰囲気もちょっと違うよね。
              普段関わらない大学と専門で互いに切磋琢磨して一つのものを作り上げる...
              なかなかない機会なので、そこに対するワクワクがあるな(山尾)。
              <br />
              今年らしい色が出たらいい、委員長のお二人についていきます！(庄司)
              <br />
              先輩たちに必死についていくだけです！(遠山)
              <br />
              今までとは変わった、新しいかまた祭を楽しみにしていただければ！(川田)
              <br />
              <br />
              普段会えない人と出会い、普段できない経験ができる、そんな非日常をぜひ体験しに来てください！
            </div>
            <div className={styles.messageImage}>
              <img src="/images/2025/message.png" alt="メッセージ" />
            </div>
          </div>
          <div className={styles.messageButton}>
            <Button title="メッセージを見る" href="/2025/message" variant="secondary" />
          </div>
          {/* 小さめの回転ドット円（メッセージセクション下部） */}
          <div className={styles.messageSpin} aria-hidden>
            <svg
              width="72"
              height="72"
              viewBox="0 0 103 103"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="51.5"
                cy="51.5"
                r="49.5768"
                stroke="#FFFFFF"
                strokeWidth="3.84647"
                strokeDasharray="7.69 7.69"
              />
            </svg>
          </div>
        </div>
        <div
          ref={liveRef}
          className={`${styles.liveSection} ${styles.fadeUp} ${isLiveVisible ? styles['fadeUp--visible'] : ''}`}
        >
          {/* <Title tag="h2" align="center">
            LIVE（当日生中継！）
          </Title>
          <div className="button__container">
            <div className="button">
              <Button title="開会式" href="/2025/events" fz="14px" />
            </div>
          </div> */}
          <div className={styles.liveSpin} aria-hidden>
            <svg
              width="103"
              height="103"
              viewBox="0 0 103 103"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="51.5"
                cy="51.5"
                r="49.5768"
                stroke="#FFFFFF"
                strokeWidth="3.84647"
                strokeDasharray="7.69 7.69"
              />
            </svg>
          </div>
        </div>
        <div
          ref={historyRef}
          className={`${styles.fadeUp} ${isHistoryVisible ? styles['fadeUp--visible'] : ''}`}
        >
          <History />
        </div>
      </div>
      {/* リンク配置になったらコメントアウト解除 */}
      {/* <Title tag="h2" align="center">
        LINKS
      </Title>
      <div className={styles.linkText}>
        <p>学校法人片柳学園</p>
        <p>学校法人片柳学園</p>
        <p>学校法人片柳学園</p>
      </div> */}
      <Footer />
      <FloatingNav />
    </main>
  );
};

export default Page;
